import { chatBot } from '@/constants/pages';
import { getSession } from '@/services/llm';
import { useRequestToken } from '@/store/useRequestToken';
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
  outlinedInputClasses,
} from '@mui/material';
import { IconPlayerStopFilled, IconSend } from '@tabler/icons-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Loader from '../loader';
import ChatMessage from './message-bubble';
import ShowMoreMarkdown from './showMoreMarkDown';
import TypingIndicatorComponent from './typeIndicator';
interface FollowUpQuestion {
  question: string;
  followUpQuestions?: FollowUpQuestion[];
}

interface PromptData {
  promptData: FollowUpQuestion[];
}
interface ChatMessageType {
  text: string;
  type: 'question' | 'answer';
  followUpQuestions?: FollowUpQuestion[];
}

interface Data {
  query: string;
  session_id: string;
}

const ChatBot: React.FC<PromptData> = ({ promptData }) => {
  const [questionsData, setQuestionsData] = useState<ChatMessageType[]>([]);
  const [showPrompt, setShowPrompt] = useState<boolean>(true);
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [session, setSession] = useState<string>('');
  const [serviceDown, setServiceDown] = useState<boolean>(false);
  const [showOtherOptions, setShowOtherOptions] = useState(true);
  const [rerender, setRerender] = useState(false);
  const [sessionLoader, setSessionLoader] = useState(false);

  const fetchSession = useCallback(async () => {
    setSessionLoader(true);
    try {
      const session = await getSession();
      setSession(session?.data?.session_id);
      setServiceDown(false);
      setSessionLoader(false);
    } catch (e) {
      setServiceDown(true);
      setSessionLoader(false);
    }
  }, []);

  const [streamInProgress, setStreamInProgress] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current && autoScroll && (rerender || !rerender)) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [autoScroll, rerender]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 30;
    setAutoScroll(atBottom);
  }, []);

  const startStreamRequest = useCallback(
    async (text: string, followUp?: FollowUpQuestion) => {
      if (inputRef?.current) inputRef.current.value = '';
      const updatedQuestions = [...questionsData];
      updatedQuestions.push({ text, type: 'question' });
      setQuestionsData(updatedQuestions);
      setAutoScroll(true);
      const url =
        process.env.NEXT_PUBLIC_LLM_BASE_URL ||
        'https://llm-dev.bridgeweave.net:458';
      const data: Data = {
        query: text,
        session_id: session,
      };
      const headers = {
        'Authorization': `Bearer ${useRequestToken.getState().requestToken}`,
        'Content-Type': 'application/json',
      };
      try {
        setLoadingSearch(true);
        setStreamInProgress(true);
        const response = await fetch(`${url}/stream`, {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        setLoadingSearch(false);
        const reader = response.body?.getReader();
        if (!reader) throw new Error('ReadableStream not supported');

        const decoder = new TextDecoder('utf-8');
        updatedQuestions.push({
          text: '',
          type: 'answer',
        });

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value);

          setRerender((r) => !r);
          updatedQuestions[updatedQuestions.length - 1] = {
            text: updatedQuestions[updatedQuestions.length - 1].text + chunk,
            type: 'answer',
          };
          setQuestionsData(updatedQuestions);
          if (inputRef?.current?.value) inputRef.current.value = '';
        }
        if (updatedQuestions[updatedQuestions.length - 1].text?.length < 1) {
          updatedQuestions[updatedQuestions.length - 1] = {
            text: 'Unable to get answer right now. Please try again.',
            type: 'answer',
            ...(followUp && { followUpQuestions: followUp?.followUpQuestions }),
          };
        } else {
          updatedQuestions[updatedQuestions.length - 1] = {
            text: updatedQuestions[updatedQuestions.length - 1].text,
            type: 'answer',
            ...(followUp && { followUpQuestions: followUp?.followUpQuestions }),
          };
        }
        setQuestionsData(updatedQuestions);
        setStreamInProgress(false);
      } catch (error) {
        updatedQuestions.push({
          text: "It seems we've encountered an issue. Please try again.",
          type: 'answer',
        });
        setLoadingSearch(false);
        setStreamInProgress(false);
      }
    },
    [questionsData, session],
  );

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    if (questionsData) {
      scrollToBottom();
    }
  }, [questionsData, scrollToBottom, streamInProgress]);
  const [indicatorStep, setIndicatorStep] = useState(0);
  const [indicatorText, setIndicatorText] = useState('Calculating');
  const loadingIndicatiorText = useMemo(
    () => ['Calculating', 'Analysing', 'Processing'],
    [],
  );
  useEffect(() => {
    if (loadingSearch && indicatorStep < 3) {
      var timer = setTimeout(() => {
        setIndicatorText(loadingIndicatiorText[indicatorStep]);
        setIndicatorStep((prev) => prev + 1);
      }, 800);
      return () => clearTimeout(timer);
    }
    if (!loadingSearch) {
      setIndicatorStep(0);
      setIndicatorText('Calculating');
    }

    return () => clearTimeout(timer);
  }, [loadingSearch, indicatorStep, loadingIndicatiorText]);

  const inputRef = useRef<HTMLInputElement>(null);

  // Handle click to show answer
  const handleQuestionClick = async (
    text: string,
    followUp?: FollowUpQuestion,
  ) => {
    if (!showOtherOptions) setShowOtherOptions(true);
    if (showPrompt) setShowPrompt(false);
    await startStreamRequest(text, followUp);
  };

  const onSearch = useCallback(
    async (str: string) => {
      if (!str) return;
      if (showPrompt) setShowPrompt(false);
      await startStreamRequest(str);
    },
    [showPrompt, startStreamRequest],
  );

  const onClickSearchBtn = useCallback(() => {
    onSearch?.(inputRef?.current?.value ?? '');
  }, [onSearch]);

  const handleShowMoreOptions = (promptData: FollowUpQuestion[]) => {
    setShowOtherOptions(false);
    const updatedQuestions = [...questionsData];
    updatedQuestions[updatedQuestions.length - 1] = {
      text: updatedQuestions[updatedQuestions.length - 1].text,
      type: 'answer',
      followUpQuestions: promptData,
    };
    setQuestionsData(updatedQuestions);
  };

  return (
    <Box position={'relative'}>
      <Box
        sx={{
          maxHeight: 'calc(80vh - 70px)',
          overflowY: 'auto',
          paddingTop: '10px',
          width: '100%',
          paddingX: 1,
          backgroundColor: 'custom.chatBotLLM.llmbgColor',
          paddingBottom: 0.3,
        }}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {sessionLoader ? (
          <Loader />
        ) : serviceDown ? (
          <Box padding='20px'>Service is down please restart Chat </Box>
        ) : (
          <Box
            display='flex'
            flexDirection='column'
            width='100%'
          >
            {promptData?.map((item, index) => {
              return (
                <Stack
                  paddingX={1}
                  paddingY={'2px'}
                  key={`prompt-data-${index}`}
                >
                  <Box
                    display={'flex'}
                    bgcolor={(theme) =>
                      streamInProgress
                        ? '#d3d3d3'
                        : theme.palette.custom.chatBotLLM.promptColor
                    }
                    sx={{ cursor: streamInProgress ? 'progress' : 'pointer' }}
                    paddingX={2}
                    paddingY={1}
                    borderRadius={1}
                    width={'90%'}
                    onClick={() => {
                      !streamInProgress &&
                        handleQuestionClick(item.question, item);
                    }}
                  >
                    <Typography
                      lineHeight='1.1'
                      fontWeight={400}
                    >
                      {item.question}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}

            {!showPrompt &&
              questionsData.map((item, index) => {
                return (
                  <Box
                    key={`Q&a${index}`}
                    display='flex'
                    flexDirection='column'
                    paddingTop={'2px'}
                  >
                    {item.type === 'question' && (
                      <Box
                        display={'flex'}
                        paddingX={1}
                        justifyContent='right'
                      >
                        <Box
                          justifyContent='right'
                          display='flex'
                          width='90%'
                        >
                          <ChatMessage type={item.type}>
                            {item.text}
                          </ChatMessage>
                        </Box>
                      </Box>
                    )}
                    {loadingSearch && questionsData.length - 1 === index && (
                      <Box
                        display={'flex'}
                        // justifyContent={'right'}
                        paddingX={2}
                        paddingY='4px'
                      >
                        <ChatMessage type='answer'>
                          <TypingIndicatorComponent size='4px'>
                            <Typography
                              component='span'
                              paddingRight='4px'
                            >
                              {indicatorText}
                            </Typography>{' '}
                            {/* Pass this 4s children */}
                          </TypingIndicatorComponent>
                        </ChatMessage>
                      </Box>
                    )}
                    {item.type === 'answer' && (
                      <Box
                        display='flex'
                        // alignItems='end'
                        paddingX={1}
                        flexDirection='column'
                      >
                        {item.text && (
                          <Box
                            width='90%'
                            display={'flex'}
                            flexDirection='column'
                            // justifyContent='left'
                            paddingY={'2px'}
                          >
                            <ChatMessage type={item.type}>
                              <ShowMoreMarkdown
                                content={item.text}
                                previewLines={5}
                                streamInProgress={
                                  questionsData.length - 1 === index
                                    ? streamInProgress
                                    : false
                                }
                              />
                            </ChatMessage>
                          </Box>
                        )}
                        {item.followUpQuestions && (
                          <Box
                            display={'flex'}
                            flexDirection={'column'}
                            width={'100%'}
                          //   paddingY={0.2}
                          >
                            {item.followUpQuestions.map(
                              (followUpQuestion, fqIdx) => {
                                return (
                                  <Box
                                    key={`fq-${fqIdx}`}
                                    display={'flex'}
                                    paddingY={'2px'}
                                    flexDirection={'column'}
                                    width={'90%'}
                                  >
                                    <Box
                                      display={'flex'}
                                      bgcolor={(theme) =>
                                        streamInProgress
                                          ? '#d3d3d3'
                                          : theme.palette.custom.chatBotLLM
                                            .promptColor
                                      }
                                      paddingX={2}
                                      paddingY={1}
                                      borderRadius={1}
                                      width={'100%'}
                                      onClick={() => {
                                        !streamInProgress &&
                                          handleQuestionClick(
                                            followUpQuestion.question,
                                            followUpQuestion,
                                          );
                                      }}
                                      sx={{
                                        cursor: streamInProgress
                                          ? 'progress'
                                          : 'pointer',
                                      }}
                                    >
                                      <Typography
                                        lineHeight='1.1'
                                        fontWeight={400}
                                      >
                                        {followUpQuestion.question}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              },
                            )}
                            {/* {showOtherOptions && (
                            <Box paddingY={0.2}>
                              <Box
                                display={'flex'}
                                bgcolor={(theme) =>
                                  theme.palette.custom.chatBotLLM.promptColor
                                }
                                paddingX={2}
                                paddingY={1}
                                borderRadius={1}
                                width={'100%'}
                                onClick={() => {
                                  handleShowMoreOptions(promptData);
                                }}
                                sx={{ cursor: 'pointer' }}
                              >
                                <Typography
                                  lineHeight='normal'
                                  fontWeight={300}
                                >
                                  Show other options
                                </Typography>
                              </Box>
                            </Box>
                          )} */}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
          </Box>
        )}
      </Box>
      <Box
        position='sticky'
        bottom={0}
        right={0}
        display='flex'
        bgcolor='custom.chatBotLLM.llmbgColor'
        padding={1}
        width='100%'
        flexDirection='column'
      >
        <TextField
          inputRef={inputRef}
          autoComplete='off'
          placeholder='Any question on investing?'
          size='small'
          disabled={loadingSearch || streamInProgress}
          onKeyUp={(e) => {
            if (e.key === 'Enter') {
              !streamInProgress &&
                handleQuestionClick(inputRef?.current?.value ?? '');
            }
          }}
          InputProps={{
            endAdornment: (
              <Box paddingLeft='2px'>
                <IconButton
                  disabled={loadingSearch || streamInProgress}
                  sx={{
                    transform: streamInProgress ? 'none' : 'rotate(45deg)',
                    backgroundColor: 'custom.chatBotLLM.headerBgColor',
                    color: 'custom.onAccentColor',
                    ['&:hover']: {
                      backgroundColor: 'custom.chatBotLLM.headerBgColor',
                    },
                  }}
                  onClick={() => !streamInProgress && onClickSearchBtn()}
                >
                  {streamInProgress ? <IconPlayerStopFilled /> : <IconSend />}
                </IconButton>
              </Box>
            ),
          }}
          sx={{
            'width': '100%',
            'borderColor': '#ffffff',
            [`& .${outlinedInputClasses.root}`]: {
              p: '0 0',
            },
            '& .MuiOutlinedInput-root': {
              'paddingRight': '0px !important',
              'backgroundColor': '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d3d3d3',
                borderWidth: '1px',
                borderRadius: 'inherit',
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'custom.global.accentColor',
                  borderWidth: '1px',
                },
              },
            },
          }}
        />
        <Box>
          <Typography
            fontWeight='300'
            fontSize='0.8rem'
            lineHeight='1.1'
            textAlign='center'
            padding={1}
          >
            {`${chatBot.title} is a virtual assistant, not a financial advisor and some of her answers may not be 100% accurate. She is powered by OpenAI.`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatBot;
